import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import type { FC, PropsWithChildren } from 'react'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import type { AccommodationDto } from '@hcr/api/consumer'
import type { DestinationDto } from '@hcr/api/optimizely'
import {
  find,
  flow,
  getFromSessionStorage,
  getPropertyValue,
  head,
  isNotNull,
  isNotUndefined,
  isUndefined,
  logger,
  removeFromSessionStorage,
  setToSessionStorage,
  sort,
} from '@hcr/utils'

import {
  useAccommodationDetailsQuery,
  useAccommodationsQuery,
  useActivitiesQuery,
  useDestinationDetailsQuery,
  useDestinationsQuery,
  useIdToken,
  useSingleTicketsQuery,
} from '../hooks'
import type { DestinationIdPathParam, UseCompoundQueryResult } from '../models'
import { compareDates, isBookingIn, isDestinationOf, isNotUndefinedOrEmptyPathParam } from '../utils'

interface Holiday {
  accommodationId: number | null // Consumer API
  destinationId: string | null // Optimizely API
}

const DEFAULT_HOLIDAY: Holiday = {
  accommodationId: null,
  destinationId: null,
}

const HOLIDAY_CONFIG = {
  destinationStorageKeyName: 'holiday-destination-id',
  accommodationStorageKeyName: 'holiday-accommodation-id',
} as const

interface HolidayContext extends UseCompoundQueryResult<Holiday> {
  setAccommodationId: (accommodationId: number) => void
  setDestinationId: (destinationId: string) => void
}

const HolidayContext = createContext<HolidayContext>({
  data: DEFAULT_HOLIDAY,
  isError: false,
  isPending: true,
  isSuccess: false,
  setAccommodationId: () => void null,
  setDestinationId: () => void null,
})

enum TRIGGER {
  INIT,
  ACCOMMODATION,
  DESTINATION,
  NONE,
}

type HolidayProviderProps = PropsWithChildren

export const HolidayProvider: FC<HolidayProviderProps> = ({ children }) => {
  const isAuthenticated = useIsAuthenticated()
  const { instance } = useMsal()
  const params = useParams<DestinationIdPathParam>()

  const [trigger, setTrigger] = useState<TRIGGER>(TRIGGER.INIT)
  const [accommodationId, setAccommodationId] = useState<number | null>(DEFAULT_HOLIDAY.accommodationId)
  const [destinationId, setDestinationId] = useState<string | null>(DEFAULT_HOLIDAY.destinationId)

  const [savedAccommodationId, setSavedAccommodationId] = useState<string | null>(() =>
    getFromSessionStorage(HOLIDAY_CONFIG.accommodationStorageKeyName)
  )

  const [savedDestinationId, setSavedDestinationId] = useState<string | null>(() =>
    getFromSessionStorage(HOLIDAY_CONFIG.destinationStorageKeyName)
  )

  useEffect(() => {
    const callbackId = instance.addEventCallback(event => {
      if (event.eventType === 'msal:logoutSuccess' || event.eventType === 'msal:logoutFailure') {
        clearTrigger()
      }
    })

    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId)
      }
    }
  }, [instance])

  const setAccommodationTrigger = (accommodationId: number) => {
    setAccommodationId(accommodationId)
    setDestinationId(null)
    setTrigger(TRIGGER.ACCOMMODATION)

    setToSessionStorage(HOLIDAY_CONFIG.accommodationStorageKeyName, String(accommodationId))
    removeFromSessionStorage(HOLIDAY_CONFIG.destinationStorageKeyName)
    setSavedAccommodationId(String(accommodationId))
    setSavedDestinationId(null)
  }

  const setDestinationTrigger = (destinationId: string) => {
    setAccommodationId(null)
    setDestinationId(destinationId)
    setTrigger(TRIGGER.DESTINATION)

    setToSessionStorage(HOLIDAY_CONFIG.destinationStorageKeyName, destinationId)
    removeFromSessionStorage(HOLIDAY_CONFIG.accommodationStorageKeyName)
    setSavedDestinationId(destinationId)
    setSavedAccommodationId(null)
  }

  const clearTrigger = () => {
    setAccommodationId(null)
    setDestinationId(null)
    setTrigger(TRIGGER.NONE)

    removeFromSessionStorage(HOLIDAY_CONFIG.destinationStorageKeyName)
    removeFromSessionStorage(HOLIDAY_CONFIG.accommodationStorageKeyName)
    setSavedDestinationId(null)
    setSavedAccommodationId(null)
  }

  const earliestAccommodation = useEarliestAccommodation()
  const firstTicketDestination = useFirstTicketDestination()

  const idToken = useIdToken()

  const accommodationIn = useAccommodationIn(destinationId)
  const destinationOf = useDestinationOf(accommodationId)

  const savedAccommodationDetails = useAccommodationDetailsQuery(
    { idToken: String(idToken), accommodationId: Number(savedAccommodationId) },
    { enabled: isNotNull(savedAccommodationId) && isNotNull(idToken) }
  )

  const savedDestinationDetails = useDestinationDetailsQuery(
    { destinationId: String(savedDestinationId) },
    { enabled: isNotNull(savedDestinationId) }
  )

  useEffect(() => {
    if (
      savedAccommodationDetails.isSuccess &&
      savedAccommodationDetails.data &&
      !accommodationId &&
      trigger === TRIGGER.INIT
    ) {
      setAccommodationId(savedAccommodationDetails.data.accommodation_id)
      setTrigger(TRIGGER.ACCOMMODATION)
    }
  }, [savedAccommodationDetails.isSuccess, savedAccommodationDetails.data, accommodationId, trigger])

  useEffect(() => {
    if (
      savedDestinationDetails.isSuccess &&
      savedDestinationDetails.data &&
      !destinationId &&
      trigger === TRIGGER.INIT
    ) {
      setDestinationId(savedDestinationDetails.data.destinationId)
      setTrigger(TRIGGER.DESTINATION)
    }
  }, [savedDestinationDetails.isSuccess, savedDestinationDetails.data, destinationId, trigger])

  useEffect(() => {
    if (savedAccommodationDetails.isError || (savedAccommodationDetails.isSuccess && !savedAccommodationDetails.data)) {
      removeFromSessionStorage(HOLIDAY_CONFIG.accommodationStorageKeyName)
      setSavedAccommodationId(null)
    }
  }, [savedAccommodationDetails.isError, savedAccommodationDetails.isSuccess, savedAccommodationDetails.data])

  useEffect(() => {
    if (savedDestinationDetails.isError || (savedDestinationDetails.isSuccess && !savedDestinationDetails.data)) {
      removeFromSessionStorage(HOLIDAY_CONFIG.destinationStorageKeyName)
      setSavedDestinationId(null)
    }
  }, [savedDestinationDetails.isError, savedDestinationDetails.isSuccess, savedDestinationDetails.data])

  // Handles TRIGGER.INIT
  useEffect(() => {
    if (trigger === TRIGGER.INIT) {
      if (isNotUndefinedOrEmptyPathParam(params.destinationId)) {
        return void setDestinationTrigger(params.destinationId)
      }

      if (isAuthenticated && earliestAccommodation.isSuccess && firstTicketDestination.isSuccess) {
        const hasSavedAccommodation = savedAccommodationId && savedAccommodationDetails.isSuccess
        const hasSavedDestination = savedDestinationId && savedDestinationDetails.isSuccess
        const savedAccommodationStillLoading = savedAccommodationId && savedAccommodationDetails.isPending
        const savedDestinationStillLoading = savedDestinationId && savedDestinationDetails.isPending

        if (savedAccommodationStillLoading || savedDestinationStillLoading) {
          return
        }

        if (hasSavedAccommodation && savedAccommodationDetails.data) {
          setAccommodationId(savedAccommodationDetails.data.accommodation_id)
          setTrigger(TRIGGER.ACCOMMODATION)
          return
        }

        if (hasSavedDestination && savedDestinationDetails.data) {
          setDestinationId(savedDestinationDetails.data.destinationId)
          setTrigger(TRIGGER.DESTINATION)
          return
        }

        if (!savedAccommodationId && !savedDestinationId) {
          if (isNotUndefined(earliestAccommodation.data)) {
            return void setAccommodationTrigger(earliestAccommodation.data.accommodation_id)
          }

          if (isNotUndefined(firstTicketDestination.data)) {
            return void setDestinationTrigger(firstTicketDestination.data.destinationId)
          }

          return void clearTrigger()
        }
      }
    } else if (trigger === TRIGGER.ACCOMMODATION) {
      if (
        isAuthenticated &&
        earliestAccommodation.isSuccess &&
        isNotUndefined(earliestAccommodation.data) &&
        destinationOf.isSuccess &&
        isUndefined(destinationOf.data)
      ) {
        logger.warn(
          `Destination details are not available for resort_id: ${earliestAccommodation.data?.resort_id}, check if provided mokkiId or hotelId are correct`
        )
        return void clearTrigger()
      }
    }
  }, [
    trigger,
    isAuthenticated,
    params.destinationId,
    savedAccommodationId,
    savedDestinationId,
    savedAccommodationDetails.isSuccess,
    savedAccommodationDetails.isPending,
    savedAccommodationDetails.data,
    savedDestinationDetails.isSuccess,
    savedDestinationDetails.isPending,
    savedDestinationDetails.data,
    earliestAccommodation.isSuccess,
    earliestAccommodation.data,
    firstTicketDestination.isSuccess,
    firstTicketDestination.data,
    destinationOf.isSuccess,
    destinationOf.data,
  ])

  // Handles TRIGGER.ACCOMMODATION & TRIGGER.DESTINATION
  const data = useMemo<Holiday>(() => {
    if (isAuthenticated) {
      if (trigger === TRIGGER.ACCOMMODATION && destinationOf.isSuccess && isNotUndefined(destinationOf.data)) {
        return { accommodationId, destinationId: destinationOf.data.destinationId }
      }

      if (trigger === TRIGGER.DESTINATION && accommodationIn.isSuccess && isNotUndefined(accommodationIn.data)) {
        return { accommodationId: accommodationIn.data.accommodation_id, destinationId }
      }
    }

    return { accommodationId, destinationId }
  }, [
    trigger,
    isAuthenticated,
    destinationId,
    destinationOf.isSuccess,
    destinationOf.data,
    accommodationId,
    accommodationIn.isSuccess,
    accommodationIn.data,
  ])

  return (
    <HolidayContext.Provider
      value={{
        data,
        isError:
          isAuthenticated &&
          ((trigger === TRIGGER.INIT && earliestAccommodation.isError) ||
            (trigger === TRIGGER.INIT && firstTicketDestination.isError) ||
            (trigger === TRIGGER.ACCOMMODATION && destinationOf.isError) ||
            (trigger === TRIGGER.ACCOMMODATION && destinationOf.isSuccess && isUndefined(destinationOf.data)) ||
            (trigger === TRIGGER.DESTINATION && accommodationIn.isError)),
        isPending:
          isAuthenticated &&
          ((trigger === TRIGGER.INIT && earliestAccommodation.isPending) ||
            (trigger === TRIGGER.INIT && firstTicketDestination.isPending) ||
            (trigger === TRIGGER.ACCOMMODATION && destinationOf.isPending) ||
            (trigger === TRIGGER.DESTINATION && accommodationIn.isPending)),
        isSuccess:
          !isAuthenticated ||
          (trigger === TRIGGER.ACCOMMODATION && destinationOf.isSuccess) ||
          (trigger === TRIGGER.DESTINATION && accommodationIn.isSuccess) ||
          trigger === TRIGGER.NONE,
        setAccommodationId: setAccommodationTrigger,
        setDestinationId: setDestinationTrigger,
      }}
    >
      {children}
    </HolidayContext.Provider>
  )
}

export const useHoliday = (): HolidayContext => {
  const context = useContext(HolidayContext)

  if (isUndefined(context)) {
    throw new Error('useHoliday must be used within HolidayProvider')
  }

  return context
}

export const useEarliestHoliday = (): UseCompoundQueryResult<Holiday> => {
  const earliestAccommodation = useEarliestAccommodation()
  const destinationOf = useDestinationOf(earliestAccommodation.data?.accommodation_id ?? null)

  return {
    data: {
      accommodationId: earliestAccommodation.data?.accommodation_id ?? null,
      destinationId: destinationOf.data?.destinationId ?? null,
    },
    isError: earliestAccommodation.isError || destinationOf.isError,
    isPending: earliestAccommodation.isPending || (earliestAccommodation.isSuccess && destinationOf.isPending),
    isSuccess: earliestAccommodation.isSuccess && destinationOf.isSuccess,
  }
}

export const useEarliestAccommodation = () => {
  const idToken = useIdToken()

  return useAccommodationsQuery(
    { idToken: String(idToken) },
    {
      select: flow(sort({ comparer: compareDates, asc: getPropertyValue('start_date') }), head),
      enabled: isNotNull(idToken),
    }
  )
}

export const useFirstTicketDestination = (): UseCompoundQueryResult<DestinationDto | undefined> => {
  const idToken = useIdToken()

  const singleTicket = useSingleTicketsQuery(
    { idToken: String(idToken) },
    {
      select: head,
      enabled: isNotNull(idToken),
    }
  )

  const activity = useActivitiesQuery(
    { idToken: String(idToken) },
    {
      select: head,
      enabled: isNotNull(idToken),
    }
  )

  const ticket = singleTicket.data || activity.data

  const destination = useDestinationsQuery({
    select: flow(getPropertyValue('destinations'), find(isDestinationOf(ticket))),
    enabled: singleTicket.isSuccess,
  })

  return {
    data: destination.data,
    isError: singleTicket.isError || destination.isError || activity.isError,
    isPending:
      singleTicket.isPending ||
      (singleTicket.isSuccess && destination.isPending) ||
      activity.isPending ||
      (activity.isSuccess && activity.isPending),
    isSuccess: singleTicket.isSuccess && destination.isSuccess && activity.isSuccess,
  }
}

const useAccommodationIn = (destinationId: string | null): UseCompoundQueryResult<AccommodationDto | undefined> => {
  const idToken = useIdToken()

  const destinationDetails = useDestinationDetailsQuery(
    { destinationId: String(destinationId) },
    { enabled: isNotNull(destinationId) }
  )

  const accommodation = useAccommodationsQuery(
    { idToken: String(idToken) },
    {
      select: flow(
        sort({ comparer: compareDates, asc: getPropertyValue('start_date') }),
        find(isBookingIn(destinationDetails.data))
      ),
      enabled: isNotNull(idToken) && destinationDetails.isSuccess,
    }
  )

  return {
    data: accommodation.data,
    isError: destinationDetails.isError || accommodation.isError,
    isPending: destinationDetails.isPending || (destinationDetails.isSuccess && accommodation.isPending),
    isSuccess: destinationDetails.isSuccess && accommodation.isSuccess,
  }
}

const useDestinationOf = (accommodationId: number | null): UseCompoundQueryResult<DestinationDto | undefined> => {
  const idToken = useIdToken()

  const accommodation = useAccommodationDetailsQuery(
    { idToken: String(idToken), accommodationId: Number(accommodationId) },
    { enabled: isNotNull(idToken) && isNotNull(accommodationId) }
  )

  const destination = useDestinationsQuery({
    select: flow(getPropertyValue('destinations'), find(isDestinationOf(accommodation.data))),
    enabled: accommodation.isSuccess,
  })

  return {
    data: destination.data,
    isError: accommodation.isError || destination.isError,
    isPending: accommodation.isPending || (accommodation.isSuccess && destination.isPending),
    isSuccess: accommodation.isSuccess && destination.isSuccess,
  }
}
